import type { Collection, CollectionItems } from 'utils/create-collection/types';

/**
 * Creates a typed Collection object that uses a special type as its keys
 * Use when defining dictionaries where its index type is not string or
 * number (but a subset of strings as keywords)
 *
 * @param {CollectionItems<T, U>} source - The source for the initial data of the collection
 */
export function createCollection<T, U extends string>(
  source: CollectionItems<T, U> = {},
): Collection<T, U> {
  const collection: CollectionItems<T, U> = source;

  /**
   * Gets an element of type T given key U
   * @param {U} key
   * @return {T}
   */
  function get(key: U): T {
    if (collection[key] === undefined) {
      throw new Error(`Requested object at key ${key} is undefined.`);
    } else {
      // TypeScript doesn't understand that at this point collection[key] is defined
      // but we know it is, so we can typecast it
      return <T>collection[key];
    }
  }

  /**
   * Gets the entire collection object (not copy)
   * @return {CollectionItems<T, U>}
   */
  function getCollection(): Partial<Record<U, T>> {
    return collection;
  }

  /**
   * Sets an item in the collection
   * @param {U} key
   * @param {T} item
   */
  function set(key: U, item: T): void {
    collection[key] = item;
  }

  /**
   * Returns the collection keys as an array of type U
   * @return {U[]}
   */
  function keys(): U[] {
    return <U[]>Object.keys(collection);
  }

  /**
   * Returns true if an object in the collection is not undefined
   * @param {U} key
   * @return boolean
   */
  function isDefined(key: U): boolean {
    return collection[key] !== undefined;
  }

  /**
   * Deletes an item from the collection
   * @param {U} key
   */
  function deleteItem(key: U) {
    delete collection[key];
  }

  return {
    deleteItem,
    get,
    getCollection,
    isDefined,
    keys,
    set,
  };
}
